import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AppProvider, useApp } from './lib/app-context';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/AccountPages';
import { AdminPage } from './pages/AdminPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { OnboardingPage } from './pages/OnboardingPage';
import { SettingsPage } from './pages/SettingsPage';
import { DisabledModule, Shell } from './pages/Shell';
import { ConversationView, MessagesHome } from './pages/Messages';
import { CallsPage } from './components/CallUi';
import { JoinGroupPage } from './pages/JoinGroupPage';
import { StarredPage } from './pages/StarredPage';
import { SpacesPage } from './pages/SpacesPage';
import { ChatProvider } from './lib/chat-context';
import './styles/tokens.css';
import './styles/app.css';

/** requireAuth: true = signed in only, false = signed out only, 'any' = both (email links). */
function Gate({ children, requireAuth }: { children: ReactNode; requireAuth: boolean | 'any' }) {
  const { me, loading, config, i18n } = useApp();
  const here = useLocation();
  if (loading) {
    return (
      <div className="auth-page" role="status">
        {i18n.t('app.loading')}
      </div>
    );
  }
  if (!config) {
    return (
      <div className="auth-page">
        <div className="card stack auth-card" role="alert">
          <p>{i18n.t('error.NETWORK')}</p>
          <button className="btn btn-primary" type="button" onClick={() => location.reload()}>
            {i18n.t('common.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (requireAuth === 'any') return <>{children}</>;
  // A group invite survives signing in: the login page returns there (fragment included).
  if (requireAuth && !me) {
    return <Navigate to="/login" replace state={here.pathname === '/join' ? { from: `/join${here.hash}` } : undefined} />;
  }
  if (!requireAuth && me) return <Navigate to={me.onboardingCompleted ? '/' : '/onboarding'} replace />;
  return <>{children}</>;
}

/** Route changes start at the top, like a page load (a long form would otherwise leave the next page scrolled). */
function ScrollReset() {
  const { pathname } = useLocation();
  // Block body: newer browsers return a Promise from scrollTo, which must not become the effect cleanup.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Messenger-first: land on chats when messaging is enabled. */
function HomeRedirect() {
  const { config } = useApp();
  return <Navigate to={config?.flags.messaging ? '/messages' : '/spaces'} replace />;
}

function MessagesRoute() {
  const { config } = useApp();
  return config?.flags.messaging ? <MessagesHome /> : <DisabledModule labelKey="nav.messages" />;
}

function ConversationRoute() {
  const { config } = useApp();
  return config?.flags.messaging ? <ConversationView /> : <DisabledModule labelKey="nav.messages" />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Gate requireAuth={false}><LoginPage /></Gate>} />
      <Route path="/register" element={<Gate requireAuth={false}><RegisterPage /></Gate>} />
      <Route path="/forgot-password" element={<Gate requireAuth={false}><ForgotPasswordPage /></Gate>} />
      <Route path="/reset-password" element={<Gate requireAuth="any"><ResetPasswordPage /></Gate>} />
      <Route path="/verify-email" element={<Gate requireAuth="any"><VerifyEmailPage /></Gate>} />
      <Route path="/onboarding" element={<Gate requireAuth><OnboardingPage /></Gate>} />
      <Route element={<Gate requireAuth><Shell /></Gate>}>
        <Route index element={<HomeRedirect />} />
        <Route path="spaces" element={<SpacesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="messages" element={<MessagesRoute />} />
        <Route path="messages/new" element={<MessagesRoute />} />
        <Route path="messages/:id" element={<ConversationRoute />} />
        <Route path="join" element={<JoinGroupPage />} />
        <Route path="starred" element={<StarredPage />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="discover" element={<DisabledModule labelKey="nav.discover" />} />
        <Route path="ai" element={<DisabledModule labelKey="nav.ai" />} />
        <Route path="*" element={<Navigate to="/spaces" replace />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppProvider>
      <ChatProvider>
        <BrowserRouter>
          <ScrollReset />
          <App />
        </BrowserRouter>
      </ChatProvider>
    </AppProvider>
  </StrictMode>,
);
